/* eslint-disable function-paren-newline */
import * as React from 'react';
import styled, { css } from 'styled-components';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import useTablePage from '../../hooks/useTablePage';
import { ModalDefaultDraggable, InputDatepickerRange, InputBaseContainer, ButtonWithLoader, SearchBar } from '../../molecules';
import { DEFAULT_DATE_FORMAT, GET_ALL_QUERY_PARAMS } from '../../constants';
import InspectorDashboardBlocks from '../InspectorDashboardBlocks';
import { FormsReportType, ReportTableType } from '../../api/reports';
import { ButtonMain, Loader } from '../../atoms';
import { getActivitiesList, getActivitiesStatusList } from '../../api/activities';
import { getCurrentUser } from '../../api/userSettings';
import formatDate, { formatDateTime } from '../../utils/formatDate';
import useManuallyUpdate from '../../hooks/useManuallyUpdate';
import DashboardLayout from '../../layouts/DashboardLayout';
import Table from '../../organisms/Table';
import { updatePermitInspection } from '../../api/inspections';
import PermitInspectionsForm from '../../forms/permitInspectionForm/PermitInspectionsForm';
import ModalInfo from '../../molecules/ModalInfo';
import InspectionsPage from '../Permits/InspectionsPage';
import useDevice from '../../hooks/useDevice';
import useUserRole from '../../hooks/useUserRole';
import PendingInspectionMobile from '../Responsive/pendingInspectionMobile';
import { setModalOpen, setSelectedInspection } from '../../redux/slices/inspectionsSlice';
import { setCurrentUserId } from '../../redux/slices/meSlice';
import { InspectionItemType } from '../../modules/clientModule/types';
import { RootState } from '../../redux/root';
import InspectionEditor from '../Reports/DailyInspectionReport/InspectionEditor/InspectionEditor';
import ShowAllInspectionSwitch from '../Reports/DailyInspectionReport/InspectionEditor/Controls/ShowAllInspectionSwitch';
import InspectionsTable from './InspectionsTable';
import useInspectionFilters from './useInspectionFilters';
import { useGetAllInspectorsQuery } from '../../redux/rtkQuery/apiSlice';

const InspectorInspectionsPage = () => {
  const [dates, setDates] = React.useState({} as { fromDate: Date; toDate: Date });
  const [toggleControl, setToggleControl] = useState(false);
  const { selectedInspection, showAllPendingInspections } = useSelector((s: RootState) => s.inspections);
  const dispatch = useDispatch();
  const { setParamsValue, rows, isFetching } = useInspectionFilters(dates, toggleControl);
  const { data = [] } = useGetAllInspectorsQuery(null);
  const { errors, control, handleSubmit } = useForm<ReportTableType>({
    mode: 'onChange',
  });
  const [tableManuallyUpdate, updateTable] = useManuallyUpdate();
  const [isValidForExport, setIsValidForExport] = React.useState(false);
  const currentUser = useSelector((s: RootState) => s.userAuth.info.id);

  React.useEffect(() => {
    setToggleControl(!toggleControl);
  }, [showAllPendingInspections]);

  React.useEffect(() => {
    if (currentUser) {
      dispatch(setCurrentUserId(currentUser));
    }
  }, [currentUser]);

  const inspectionActivityID = [];
  const activityIds = data => {
    inspectionActivityID.push(data);
  };

  const activityStatus = [];
  const [key, setKey] = React.useState(1);
  const onChangeKey = () => {
    setKey(key + 1);
  };

  const [isPermitModalOpen, setPermitModal] = React.useState(false);
  const [permitInspectionCount, setPermitInspectionCount] = React.useState<number | null>(null);

  const [permit, setPermit] = React.useState(null);

  const {
    isModalOpen,
    modalError,
    modalSuccess,
    modalTitle,
    modalErrorTitle,
    values,
    createOrUpdate,
    handleEdit,
    handleCloseModalInfo,
    handleCloseModal,
    setValues,
  } = useTablePage({
    updateEndpoint: updatePermitInspection,
    modalUpdateTitle: 'Update Inspection',
  });
  const openEditor = (inspection: InspectionItemType) => {
    // getPendingInspections' transformResponse overwrites `inspectionType` with the
    // type NAME (for the table column/filter) and keeps the id in `inspectionTypeId`.
    // The editor expects the id, as the dashboard passes it — handing it the name
    // made InspectionType.tsx see a "changed" type on open and pop the
    // "Changing Inspection-Type will reset all the Inspection-Notes" confirmation.
    dispatch(
      setSelectedInspection({
        ...inspection,
        inspectionType: (inspection as any).inspectionTypeId ?? inspection.inspectionType,
      }),
    );
    dispatch(setModalOpen(true));
  };
  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      setPermitModal(false);
      setPermitInspectionCount(null);
      handleCloseModal();
    }
  };
  const [isIpad] = useDevice();
  const { isSuperAdmin, isFranchiseAdmin, isEmployee, isInspector } = useUserRole();
  const handleCellClick = rowValue => {
    if ((!isIpad && !isInspector) || (!isIpad && isInspector)) {
      setPermit(rowValue.permit);
      setPermitModal(true);
    }
  };

  const [addInspection, setAddInspection] = React.useState(null);
  const [projectId, setProjectId] = React.useState('');
  const closeInfoModal = () => {
    setPermitModal(false);
    updateTable();
    handleCloseModalInfo();
  };

  React.useEffect(() => {
    Promise.all([
      getActivitiesList({
        ...GET_ALL_QUERY_PARAMS,
        activityType: 8,
      }),
      getActivitiesStatusList({
        ...GET_ALL_QUERY_PARAMS,
      }),
    ]).then(([activitiesList, activitiesStatusList]) => {
      const activitiesTitles = activitiesList.data.results?.filter(el => el?.isActiveStatus === 'True');

      activitiesTitles.map(item => {
        for (const key in item) {
          if (`${key}` === 'id') {
            activityIds(`${item[key]}`);
          }
        }
      });

      const activitiesStatusLists = activitiesStatusList.data.results?.filter(
        el => el?.isActiveStatus === 'True' && el?.name === 'Pending',
      );

      // updating params to trigger RTKQLuery call
      setParamsValue(currentUser);
    });
  }, []);

  return (
    <DashboardLayout>
      <Container>
        <Toolbar>
          {selectedInspection.id && <InspectionEditor />}
          <FormContainer autoComplete="off" aria-autocomplete="none">
            <InputBaseContainer>
              <Controller
                name="range"
                defaultValue=""
                control={control}
                render={({ value, name, onChange, onBlur }) => (
                  <InputDatepickerRange
                    name={name}
                    placeholderText="Select Date"
                    error={errors.range || errors.fromDate || errors.toDate}
                    dateFormat={DEFAULT_DATE_FORMAT}
                    onRangeChange={(start, end) => {
                      if (start && end) {
                        onChange([start, end]);
                        setDates({ fromDate: start, toDate: end });

                        updateTable();
                      } else {
                        setDates({} as { fromDate: Date; toDate: Date });
                      }
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
            </InputBaseContainer>
          </FormContainer>
          <SwitchWrapper>
            <LoaderWrapper>{isFetching && <Loader />}</LoaderWrapper>

            <ShowAllInspectionSwitch disabled={false} />
          </SwitchWrapper>
        </Toolbar>
        {currentUser && <PendingInspectionMobile openEditor={openEditor} />}
        {currentUser && (
          <TableCard>
            <InspectorTableWrapper>
              <InspectionsTable rows={rows} isFetching={isFetching} onCellClick={handleCellClick} openEditor={openEditor} />
            </InspectorTableWrapper>
          </TableCard>
        )}
        {isModalOpen && !isPermitModalOpen && (
          <ModalDefaultDraggable title={modalTitle} onClose={handleCloseForm}>
            <PermitInspectionsForm
              // <InspectorPermitInspectionsForm
              onSubmit={createOrUpdate}
              initialValues={values}
              modalError={modalError}
              addButtonText="Create"
              updateButtonText="Update"
              initialErrors={errors}
              permit={permit}
              // isReadOnly={true}
              setInitialValues={setValues}
              updateTable={updateTable}
            />
          </ModalDefaultDraggable>
        )}
        {isPermitModalOpen && (
          <ModalDefaultDraggable
            title={
              permitInspectionCount === null
                ? 'Permit Inspections'
                : `Permit Inspections (${permitInspectionCount})`
            }
            onClose={handleCloseForm}
            wide
          >
            <InspectionsPage
              projectId={projectId}
              permit={permit}
              isAddInspection={false}
              setAddInspection={setAddInspection}
              isReadOnly
              onCountChange={setPermitInspectionCount}
            />
          </ModalDefaultDraggable>
        )}
        {(modalSuccess || modalError) && (
          <ModalInfo timeToClose={5000} isError={modalError} onClose={closeInfoModal} errorMsg={modalErrorTitle} />
        )}
      </Container>
    </DashboardLayout>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const InspectorTableWrapper = styled.div`
  @media only screen and (max-width: 1366px) {
    display: none;
  }
  @media only screen and (max-width: 390px) {
    font-size: 14px;
  }
`;

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px 0;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    padding: 12px 12px 0;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px 10px 0;
  }
`;

const Toolbar = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  @media only screen and (max-width: 840px) {
    padding: 12px 14px;
    margin-bottom: 12px;
    gap: 12px;
  }

  @media only screen and (max-width: 540px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TableCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 16px;
  margin-bottom: 20px;

  @media only screen and (max-width: 840px) {
    padding: 0;
    background: transparent;
    border: 0;
    box-shadow: none;
  }
`;

const SwitchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media only screen and (max-width: 540px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const LoaderWrapper = styled.div`
  zoom: 0.6;
  position: relative;
  top: 21px;

  @media only screen and (max-width: 540px) {
    top: 0;
  }
`;

const FormContainer = styled.form`
  display: flex;
  position: relative;
  align-items: center;
  gap: 16px;

  > div {
    width: auto;
    padding: 0;
    margin: 0;
  }

  @media only screen and (max-width: 540px) {
    width: 100%;

    > div {
      width: 100%;
    }
  }
`;

export default InspectorInspectionsPage;
